import { useCallback, useEffect, useRef, useState } from "react";
import {
  cancelCentralLinkedImportJob,
  getActiveCentralLinkedImportJob,
  getCentralLinkedImportJob,
  startCentralLinkedImportJob,
} from "../api/client";
import {
  clearActiveCentralLinkedImportJob,
  loadActiveCentralLinkedImportJob,
  saveActiveCentralLinkedImportJob,
} from "../utils/centralLinkedImportJobStorage";

const POLL_MS = 2000;
const TERMINAL = new Set(["completed", "failed", "cancelled"]);

export function formatCentralLinkedDuration(ms) {
  if (ms == null || !Number.isFinite(Number(ms)) || Number(ms) < 0) return "—";
  const totalSec = Math.floor(Number(ms) / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}min`;
  if (m > 0) return `${m}min ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

function liveElapsedMs(job, loading) {
  const startedAt = job?.startedAt;
  if (!startedAt) return job?.elapsedMs ?? null;
  const t0 = new Date(startedAt).getTime();
  if (!Number.isFinite(t0)) return job?.elapsedMs ?? null;
  if (loading || isActiveStatus(job?.status)) {
    return Math.max(0, Date.now() - t0);
  }
  return job?.elapsedMs ?? Math.max(0, Date.now() - t0);
}

function extractPayload(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (raw.data !== undefined && raw.data !== null && typeof raw.data === "object") return raw.data;
  return raw;
}

function isActiveStatus(status) {
  const st = String(status || "");
  return st === "queued" || st === "running" || st === "cancel_requested";
}

function mapJobItems(items) {
  return (Array.isArray(items) ? items : []).map((row) => ({
    workspace: row.workspace,
    projectId: row.projectId,
    projectName: row.projectName || "",
    status: row.status || "pending",
    errorMessage: row.errorMessage || null,
    centralCursor: row.centralCursor || 0,
    productsTotal: row.productsTotal ?? null,
    productsSynced: row.productsSynced ?? 0,
    itemProgress: row.itemProgress ?? 0,
    processed: row.processed ?? null,
    created: row.created ?? null,
    updated: row.updated ?? null,
  }));
}

export default function useCentralLinkedImportJob({ sourceProjectId, onCompleted, onTerminal }) {
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [clockTick, setClockTick] = useState(0);
  const pollRef = useRef(null);
  const onCompletedRef = useRef(onCompleted);
  const onTerminalRef = useRef(onTerminal);
  onCompletedRef.current = onCompleted;
  onTerminalRef.current = onTerminal;

  const clearPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const applyJobState = useCallback((nextJob, nextJobId) => {
    const jid = nextJobId || nextJob?.jobId || null;
    setJobId(jid);
    setJob(nextJob || null);
    setSummary(mapJobItems(nextJob?.items));
    if (jid && nextJob && isActiveStatus(nextJob.status)) {
      saveActiveCentralLinkedImportJob({
        jobId: jid,
        sourceProjectId: nextJob.sourceProjectId ?? sourceProjectId,
        phase: nextJob.status,
      });
    }
    if (nextJob && TERMINAL.has(String(nextJob.status || ""))) {
      clearActiveCentralLinkedImportJob();
    }
  }, [sourceProjectId]);

  const pollOnce = useCallback(
    async (jid) => {
      const raw = await getCentralLinkedImportJob(jid, { light: true });
      const payload = extractPayload(raw) || raw;
      applyJobState(payload, jid);
      const st = String(payload?.status || "");
      if (TERMINAL.has(st)) {
        clearPoll();
        setLoading(false);
        if (st === "completed" && onCompletedRef.current) {
          await onCompletedRef.current(payload);
        }
        if (onTerminalRef.current) {
          onTerminalRef.current(payload, st);
        }
      }
      return payload;
    },
    [applyJobState, clearPoll]
  );

  const attachPolling = useCallback(
    (jid) => {
      clearPoll();
      setLoading(true);
      pollOnce(jid);
      pollRef.current = setInterval(() => {
        pollOnce(jid).catch(() => {});
      }, POLL_MS);
    },
    [clearPoll, pollOnce]
  );

  const resumeActiveJob = useCallback(async () => {
    try {
      const stored = loadActiveCentralLinkedImportJob();
      const activeRaw = await getActiveCentralLinkedImportJob({ light: true });
      const activePayload = extractPayload(activeRaw) || activeRaw;
      const activeId = activePayload?.jobId || stored?.jobId || null;
      if (!activeId) return null;

      const jobPayload =
        activePayload?.job ||
        (await pollOnce(activeId)) ||
        null;
      if (jobPayload && isActiveStatus(jobPayload.status)) {
        attachPolling(activeId);
        return jobPayload;
      }
      if (jobPayload && TERMINAL.has(String(jobPayload.status || ""))) {
        applyJobState(jobPayload, activeId);
        if (onTerminalRef.current) {
          onTerminalRef.current(jobPayload, String(jobPayload.status));
        }
      }
      return jobPayload;
    } catch {
      return null;
    }
  }, [applyJobState, attachPolling, pollOnce]);

  useEffect(() => {
    resumeActiveJob();
    return () => clearPoll();
  }, [clearPoll, resumeActiveJob]);

  useEffect(() => {
    const active = loading || (job && isActiveStatus(job.status));
    if (!active) return undefined;
    const id = setInterval(() => setClockTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [loading, job]);

  const startJob = useCallback(async ({ syncMode = "full" } = {}) => {
    if (!sourceProjectId) {
      throw new Error("Nenhuma empresa/projeto selecionado para atualizar workspaces vinculados.");
    }

    setLoading(true);
    setDialogOpen(true);
    setSummary(null);
    setJob(null);

    try {
      const raw = await startCentralLinkedImportJob(sourceProjectId, { syncMode });
      const payload = extractPayload(raw) || raw;

      if (payload?.code === "CENTRAL_LINKED_JOB_ACTIVE" && payload?.activeJobId) {
        setDialogOpen(true);
        attachPolling(payload.activeJobId);
        return payload;
      }

      if (payload?.accepted && payload?.jobId) {
        saveActiveCentralLinkedImportJob({
          jobId: payload.jobId,
          sourceProjectId,
          phase: "queued",
        });
        attachPolling(payload.jobId);
        return payload;
      }

      if (payload?.job) {
        applyJobState(payload.job, payload.jobId);
        setLoading(false);
        if (String(payload.job.status) === "completed" && onCompletedRef.current) {
          await onCompletedRef.current(payload.job);
        }
        return payload;
      }

      throw new Error(payload?.message || "Não foi possível iniciar a atualização.");
    } catch (err) {
      const body = err?.data && typeof err.data === "object" ? err.data : err;
      if (body?.code === "CENTRAL_LINKED_JOB_ACTIVE" && body?.activeJobId) {
        setDialogOpen(true);
        attachPolling(body.activeJobId);
        return body;
      }
      setLoading(false);
      throw err;
    }
  }, [applyJobState, attachPolling, sourceProjectId]);

  const cancelJob = useCallback(async () => {
    const jid = String(jobId || job?.jobId || "").trim();
    if (!jid) return { ok: false, reason: "missing_job_id" };

    await cancelCentralLinkedImportJob(jid);

    let payload;
    try {
      payload = await pollOnce(jid);
    } catch {
      payload = null;
    }

    const st = String(payload?.status || "");
    if (TERMINAL.has(st)) {
      clearPoll();
      setLoading(false);
      if (onTerminalRef.current) {
        onTerminalRef.current(payload, st);
      }
      return { ok: true, status: st };
    }

    setJob((prev) =>
      prev
        ? {
            ...prev,
            cancelRequested: true,
            status: st === "queued" || st === "cancel_requested" ? "cancel_requested" : prev.status,
          }
        : {
            jobId: jid,
            cancelRequested: true,
            status: "cancel_requested",
          }
    );

    return { ok: true, status: st || "cancel_requested" };
  }, [job, jobId, pollOnce, clearPoll]);

  const openProgressDialog = useCallback(() => {
    const st = String(job?.status || "");
    if (
      jobId ||
      summary?.length ||
      isActiveStatus(st) ||
      TERMINAL.has(st)
    ) {
      setDialogOpen(true);
    }
  }, [job, jobId, summary]);

  const closeProgressDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const isRunning = loading || (job && isActiveStatus(job.status));
  const elapsedMs = liveElapsedMs(job, Boolean(isRunning));
  void clockTick;

  return {
    jobId,
    job,
    summary,
    loading: Boolean(isRunning),
    elapsedMs,
    estimatedRemainingMs: job?.estimatedRemainingMs ?? null,
    overallProgress: job?.overallProgress ?? job?.progress ?? 0,
    dialogOpen,
    setDialogOpen,
    startJob,
    cancelJob,
    resumeActiveJob,
    openProgressDialog,
    closeProgressDialog,
    setSummary,
  };
}
